import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'

export { default as Button } from './Button.vue'

export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center whitespace-nowrap select-none',
    // 600 + tight tracking + line-height 1, per the canonical base style.
    // `leading-none` matters: without it the text-* utilities bring their own
    // line-height and the label no longer sits centred in a fixed-height box.
    'font-ds font-semibold leading-none tracking-ds-snug',
    'rounded-ds-md border',
    // The canonical component animates background, shadow and transform — the
    // transform being the 1px press. `transition-colors` alone drops both.
    'transition-[background-color,border-color,box-shadow,transform]',
    'duration-ds-fast ease-ds-standard',
    'focus-visible:outline-none focus-visible:shadow-ds-focus',
    // Press: 1px down, no colour inversion.
    'active:translate-y-px',
    'cursor-pointer',
    'disabled:pointer-events-none disabled:opacity-50',
    'disabled:active:translate-y-0',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: [
          'bg-primary text-primary-foreground border-primary',
          'hover:bg-primary-hover hover:border-primary-hover',
          // Canonical --brand-active. The 1px translate in the base is the press
          // affordance every variant gets; the brand fill also deepens a step,
          // which is the one colour change the design system defines for a press.
          'active:bg-primary-pressed active:border-primary-pressed',
        ].join(' '),
        // Canonical `secondary`: a white card surface with a hairline border,
        // hovering to the sunken neutral. Not a filled grey button.
        secondary: [
          'bg-card text-foreground border-input',
          'hover:bg-muted',
        ].join(' '),
        // Canonical `ghost` is Iris text on transparent — a quiet *brand*
        // action, not a neutral one. `text-brand`, not `text-primary`: the fill
        // token is too deep to read as text on a dark ground (3.19:1).
        ghost: [
          'bg-transparent text-brand border-transparent',
          'hover:bg-accent',
        ].join(' '),
        // Canonical `danger`.
        destructive: [
          'bg-destructive text-destructive-foreground border-destructive',
          'hover:bg-destructive-hover hover:border-destructive-hover',
        ].join(' '),
        // LOCAL ADDITION — not in the canonical component.
        link: [
          'bg-transparent text-brand border-transparent',
          'underline-offset-4 hover:underline',
        ].join(' '),
      },
      size: {
        // Heights, paddings, gaps and font sizes are the canonical `sizes` map:
        //   sm 32h / 12px / 14fs / 6gap
        //   md 40h / 16px / 16fs / 8gap
        //   lg 48h / 22px / 18fs / 10gap
        sm: 'h-8 gap-1.5 px-3 text-sm',
        md: 'h-10 gap-2 px-4 text-base',
        // 22px is off the 4px grid, so it stays an explicit value rather than
        // being rounded to px-5 (20px) or px-6 (24px).
        lg: 'h-12 gap-2.5 px-[1.375rem] text-lg',
        // LOCAL ADDITIONS — square icon-only buttons, heights matched to the
        // text sizes above.
        'icon-sm': 'h-8 w-8 [&>svg]:size-4',
        icon: 'h-10 w-10 [&>svg]:size-5',
        'icon-lg': 'h-12 w-12 [&>svg]:size-6',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
